import {
  DayOfWeek,
  GroupFilterDto,
  WorkflowStatus,
} from 'src/interfaces/workflows.interface';

// At 200 requests per batch, we're at around 4 seconds per batch.
export const ADD_CONTACTS_MAX_BATCH_SIZE = 200;

export enum CommunicationChannel {
  CALL = 'call',
  EMAIL = 'email',
  SMS = 'sms',
}

export type Contact = {
  toNumber: string;
  toEmail: string;
  context: Record<string, unknown>;
};

export type CampaignSchedule = {
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  timeZone: string;
};

export type CallCampaignConfig = {
  fromNumbers: string[];
};

export type EmailCampaignConfig = {
  provider: string;
  fromEmail: string;
};

export type CrmCampaignConfig = {
  provider: string;
  recordCommunications: boolean;
  isAutoSyncContactsEnabled: boolean;
  newRecordFilter?: GroupFilterDto;
};

export type SmsCampaignConfig = {
  provider: string;
  fromNumber: string;
};

export type InitialCallCommunicationConfig = {
  agentId: string;
  scheduleAfterHours: number;
};

export type CallCommunicationConfig = {
  agentId: string;
};

export type EmailCommunicationConfig = {
  subject: string;
  body: string;
};

export type SmsCommunicationConfig = {
  body: string;
};

export type FollowUpCommunicationConfig = {
  scheduleAfterDays: number;
  contactVia: CommunicationChannel;
  callConfig?: CallCommunicationConfig;
  emailConfig?: EmailCommunicationConfig;
  smsConfig?: SmsCommunicationConfig;
};

export type Campaign = {
  name: string;
  referenceId: string;
  description?: string;
  isActive: boolean;
  status: WorkflowStatus;

  /** Campaign start date (yyyy-MM-dd). */
  campaignStartDate?: string;
  /** IANA timezone for interpreting campaignStartDate. */
  campaignStartDateTz?: string;
  contactWindowId?: string;
  schedule?: CampaignSchedule;

  initialCallConfig: InitialCallCommunicationConfig;
  followUpCommunicationConfigs: FollowUpCommunicationConfig[];

  callCampaignConfig: CallCampaignConfig;
  emailCampaignConfig?: EmailCampaignConfig;
  smsCampaignConfig?: SmsCampaignConfig;
  crmCampaignConfig?: CrmCampaignConfig;
};

export type UpdateCampaignDto = Omit<Campaign, 'isActive' | 'status'>;

export class CampaignNotFoundError extends Error {
  constructor(message: string = 'Campaign not found') {
    super(message);
    this.name = 'CampaignNotFoundError';
  }
}

export class CampaignAlreadyExistsError extends Error {
  constructor(referenceId: string) {
    super(`Campaign already exists with ID: ${referenceId}`);
    this.name = 'CampaignAlreadyExistsError';
  }
}

export class CampaignValidationError extends Error {
  constructor(message: string = 'Campaign is invalid') {
    super(message);
    this.name = 'CampaignInvalidError';
  }
}
